import React, { FC } from 'react';
import { Link } from 'react-router';
import { Breadcrumbs, Faq, HeroImage } from '../../customFrontend/parts';
import { SiteManifest, SiteManifestPage } from '../../customFrontend/types';
import { CityPills, Cta, FinalCta, SiteFooter } from './Chrome';
import { ChatappCity, ChatappLabels, ChatappTopic } from './types';
import './chatapp.scss';

interface Props {
  site: SiteManifest;
  page: SiteManifestPage;
  city: ChatappCity;
  cities: ChatappCity[];
  labels: ChatappLabels;
  /** Topic pages, in the footer row */
  topics?: ChatappTopic[];
}

/**
 * A chatapp city page. The hero follows the photo: a landscape photo is a wide
 * banner with the text on a card overlapping it (`cx-hero--wide`); a portrait
 * stands as a rounded 4:5 frame beside the text from 992px, on the left for
 * every other city (`cx-hero--flip`, by position in `cities`).
 */
const BrandCityPage: FC<Props> = ({ site, page, city, cities, labels, topics = [] }) => {
  const wide = page.imageWidth > page.imageHeight;
  const flip = cities.findIndex((candidate) => candidate.path === city.path) % 2 === 1;
  const text = (
    <div className="cx-hero-text">
      <Breadcrumbs items={[{ label: site.homeBreadcrumb ?? 'Home', to: '/' }, { label: page.breadcrumb ?? city.name }]} />
      <p className="cx-eyebrow"><span className="cx-dot" aria-hidden="true" /> {city.kicker}</p>
      <h1>{city.h1}</h1>
      <p className="cx-lead">{city.lead}</p>
      <Cta>{city.cta.button}</Cta>
    </div>
  );
  return (
    <>
      {wide ? (
        <section className={`cx-hero cx-hero--city cx-hero--wide cx-city--${city.slug}`}>
          <figure className="cx-banner">
            <HeroImage page={page} className="cx-banner-img" />
          </figure>
          <div className="container">
            <div className="cx-hero-card">{text}</div>
          </div>
        </section>
      ) : (
        <section className={`cx-hero cx-hero--city cx-hero--portrait${flip ? ' cx-hero--flip' : ''} cx-city--${city.slug}`}>
          <div className="container cx-hero-grid">
            {text}
            <div className="cx-hero-media">
              <figure className="cx-frame cx-frame--portrait">
                <HeroImage page={page} className="cx-frame-img" />
              </figure>
            </div>
          </div>
        </section>
      )}

      <article className="cx-section cx-city-article">
        <div className="container cx-narrow">
          <div className="cx-intro">{city.intro}</div>
          {city.sections.map((section) => (
            <section key={section.title} className="cx-city-section">
              <h2>{section.title}</h2>
              {section.body}
            </section>
          ))}
        </div>
      </article>

      <section className="cx-section cx-section--soft">
        <div className="container cx-narrow">
          <Faq title={labels.cityFaqTitle(city.name)} items={city.faq} />
        </div>
      </section>

      <FinalCta {...city.cta} className={`cx-city--${city.slug}`} />

      <section className="cx-section">
        <div className="container cx-narrow">
          <h2 className="text-center">{labels.moreCities}</h2>
          <CityPills cities={cities} exclude={city.path} labels={labels} />
          <p className="cx-back-home"><Link to="/">{labels.backHome}</Link></p>
        </div>
      </section>

      <SiteFooter site={site} cities={cities} labels={labels} topics={topics} />
    </>
  );
};

export default BrandCityPage;
